/**
 * Survival: waves in a fenced stretch of the coastal shelf.
 *
 * Everything here is rules over content the engine already understands. It
 * spawns through `Actors.spawnEnemy` — the same call the authored zone loop
 * makes — so an enemy in the arena is the same creature as one in the campaign,
 * with the same behaviour and the same drops. That is the point: not a second
 * game, a second set of rules over one game.
 *
 * **Run power cannot leave the run**, and it is structural rather than
 * remembered. `persists` is false, which makes `World.save()` return before it
 * writes anything and routes every salvage gain into a purse that declines. The
 * upgrade cards are `Modifier{source:'run'}` on the player's stack, and that
 * stack has no serialiser at all. There is nothing to leak through.
 */

import { SURVIVAL_CONTENT, type ModeRules } from './mode';
import {
  ALIVE_CAP, GLOBAL_CAP, RUN_CARDS, pickMix, waveAt,
  type RunCard, type WaveCarrier,
} from './waves';
import { emptyLevels, statsFor } from './upgrades';
import { floorAt } from './field';
import { Rng } from '../core/noise';
import { bank } from './profile';
import type { World } from './world';

/** Seconds of calm between waves. The HUD counts this down. */
export const INTERMISSION = 12;
// A card after EVERY wave. It was every other one, which meant the player's
// power curve climbed at half the rate the tide did — and the cards are the
// only power a run has, since nothing carries in from the profile.

interface RunState {
  wave: number;
  phase: 'fight' | 'gap' | 'over';
  /** Seconds elapsed in the current phase. */
  t: number;
  /** Bodies this wave has released so far, against the wave's budget. */
  sent: number;
  /** Carriers still owed by this wave, released across its budget. */
  carriers: WaveCarrier[];
  /** Budget fraction at which the next carrier goes in. */
  nextCarrierAt: number;
  /** Fractional spawn credit, so the front fills at a rate in seconds. */
  spawnAcc: number;
  score: number;
  chain: number;
  chainT: number;
  /** Cards on offer, or empty. */
  offer: RunCard[];
  /** Rank already taken per card id. */
  taken: Map<string, number>;
  wavesUntouched: number;
  hurtThisWave: boolean;
  bossBanner: number;
  outsideFor: number;
  /** 0..1, how close to the fence. Drives the HUD chevrons. */
  warn: number;
  rng: Rng;
}

function blankRun(): RunState {
  return {
    wave: 0, phase: 'gap', t: INTERMISSION - 3, sent: 0, carriers: [], nextCarrierAt: 0, spawnAcc: 0,
    score: 0, chain: 0, chainT: 0, offer: [], taken: new Map(),
    wavesUntouched: 0, hurtThisWave: false, bossBanner: 0,
    outsideFor: 0, warn: 0, rng: new Rng(0x5EA),
  };
}

let run: RunState = blankRun();

/** Read by the HUD and the ending card. */
export function survivalRun() { return run; }

/** Cards the player may still take, with the rank they would get. */
/**
 * Seconds between the last enemy dying and the card screen appearing.
 *
 * `endWave` fires on the frame the last kill lands, and the shell opens the
 * cards the moment an offer exists — so the wave ended and a menu was on top of
 * it in the same frame. The player never got to see the thing they had just
 * done: the last body still coming apart, the debris, the water going quiet.
 *
 * The gap is already held open while an offer is pending, so this costs nothing
 * but the beat. `run.t` advances through it, which is what lets the delay be
 * measured at all.
 */
const CARD_DELAY = 2.8;

export function survivalOffer(): { card: RunCard; rank: number }[] {
  if (run.phase !== 'gap' || run.t < CARD_DELAY) return [];
  return run.offer.map((c) => ({ card: c, rank: (run.taken.get(c.id) ?? 0) + 1 }));
}

/**
 * The run's fixed loadout. Deliberately independent of the profile: a run is
 * not a place to bring a maxed campaign boat, and not a place to grind one
 * either. Level 1 across the board, on whichever hull is owned — so the Dray is
 * a different tool here too, not a bigger number.
 */
const RUN_LEVELS = {
  ...emptyLevels(),
  // Shield high on purpose: it is the resource the mode is *about*. A tide you
  // cannot out-damage has to be one you can out-last, and a level-1 shield in
  // front of a level-1 hull gave the player nothing to survive on and nothing
  // to watch. It regenerates, so it is a rhythm — break contact, let it come
  // back, go again — rather than a number that only ever falls.
  hull: 3, shield: 4, engine: 3,
  // And a gun that can actually clear a front.
  cannon: 4, torpedo: 3, sonar: 2, lamp: 2,
};

/** Take one of the offered cards. */
export function survivalPick(world: World, id: string) {
  const card = run.offer.find((c) => c.id === id);
  if (!card) return;
  const rank = (run.taken.get(id) ?? 0) + 1;
  run.taken.set(id, rank);
  const p = world.player;
  p.mods.grant({ id: card.id, source: 'run', time: Infinity, ...card.build(rank) });
  // `applyStats` re-folds the stack over the base and re-clamps the fills, so a
  // card that raises maxHull is felt immediately rather than at the next repair.
  p.applyStats(statsFor(RUN_LEVELS, p.chassis));
  run.offer = [];
  world.showToast(card.name, card.ranks[Math.min(rank, card.ranks.length) - 1], '#c98cff');
}

/**
 * Which end of the arena the tide comes in from. The east one, always.
 *
 * This was "the far half from the player", which sounds fair and plays as
 * random: the opening wave draws you east, you stay east, and everything after
 * it arrives behind you from the west. Then it was east with a quarter flanking
 * west, which was better and still wrong — a flank you cannot predict is just a
 * thing that bites you while you are facing the front.
 *
 * The arena has a deep end and the deep end is where they come from. One
 * direction, learnable in three seconds, and the whole mode becomes "hold this
 * heading and do not let them past" instead of "spin and hope". Which way the
 * boat should be pointing is the one thing the player never has to wonder.
 */
function spawnX(): number {
  const b = SURVIVAL_CONTENT.bounds!;
  // OUTSIDE the player's fence, not inside it.
  //
  // Spawning within the arena meant a player who had drifted east — which is
  // what fighting a tide from the east makes you do — had things appear almost
  // on top of him. The fence holds the player at x1; the tide enters 45 to 95 m
  // beyond it and swims in. So there is always an approach to watch, the spawn
  // point can never be camped, and how much room you give yourself is a
  // decision rather than an accident.
  return b.x1 + run.rng.range(45, 95);
}

/**
 * One pass over the pool per frame instead of one per spawn attempt.
 *
 * The front is topped up several times a frame now, and both the global cap and
 * the per-species cap have to be checked each time. Re-walking a 150-strong
 * pool for every one of those was the whole reason to count once.
 */
const liveBy = new Map<string, number>();
let liveAll = 0;

function census(world: World) {
  liveBy.clear();
  liveAll = 0;
  for (const e of world.actors.enemies) {
    if (!e.alive) continue;
    liveAll++;
    liveBy.set(e.type, (liveBy.get(e.type) ?? 0) + 1);
  }
}

/**
 * How deep a body enters, given the species' band.
 *
 * `band` is relative to the player, so the front arrives level with the boat
 * instead of filling the column from seabed to surface. Two clamps keep it
 * honest: never inside the floor, and never out of the water — a band centred
 * on a player hugging the seabed would otherwise put half the wave in rock, and
 * one centred on a player at the surface would put half of it in the air.
 *
 * `spawnEnemy` still refuses anything that lands in stone, so the floor clamp
 * is about not wasting the attempt rather than about correctness.
 */
function spawnY(world: World, x: number, band: [number, number]): number {
  const floorHere = floorAt(x);
  const b = SURVIVAL_CONTENT.bounds!;
  const centre = world.player.y + run.rng.range(band[0], band[1]);
  const lo = floorHere + 5;
  const hi = Math.min(b.y1 - 5, floorHere + 52);
  return Math.min(hi, Math.max(lo, centre));
}

/** Put one body in the water at the eastern edge. Returns false if capped. */
function release(world: World, type: string, band: [number, number], tough: number,
                 carrier?: WaveCarrier): boolean {
  if (liveAll >= GLOBAL_CAP) return false;
  if ((liveBy.get(type) ?? 0) >= (ALIVE_CAP[type as never] ?? 8)) return false;
  const x = spawnX();
  const y = spawnY(world, x, band);
  const e = world.actors.spawnEnemy(type as never, x, y, run.rng, {
    tough,
    // Swarmers flock by group, and one group per wave means the whole shoal
    // coheres into a single mass rather than a scatter of loners.
    group: type === 'swarmer' ? 400 + run.wave : undefined,
    // The one line that turns the campaign's ambushers into a tide.
    march: true,
    carrier: carrier?.drop,
  });
  if (!e) return false;
  liveAll++;
  liveBy.set(type, (liveBy.get(type) ?? 0) + 1);
  run.sent++;
  return true;
}

function beginWave(world: World, n: number) {
  const w = waveAt(n);
  run.wave = n;
  run.phase = 'fight';
  run.t = 0;
  run.sent = 0;
  run.spawnAcc = 0;
  run.hurtThisWave = false;
  run.bossBanner = w.boss ? 3.2 : 0;
  // Carriers are spread across the budget rather than sent at the start, so
  // there is a reason to keep looking at the front instead of clearing it.
  run.carriers = (w.carriers ?? []).flatMap((c) =>
    Array.from({ length: c.count }, () => c));
  run.nextCarrierAt = run.carriers.length ? 0.18 : 2;
  if (w.octopus) {
    // Un-retire and rouse it. `reset` restores the tentacles and clears the
    // defeated flag `begin` set at the start of the run.
    world.octopus.reset();
    world.octopus.wake();
  }
  world.showToast(w.boss ? 'BOSS WAVE' : `WAVE ${n}`, w.name, w.boss ? '#ff8a5c' : '#5fe4e0');
}

function endWave(world: World) {
  run.score += 200 * run.wave;
  if (!run.hurtThisWave) { run.score += 500; run.wavesUntouched++; }
  run.phase = 'gap';
  run.t = 0;
  world.actors.compact();
  // Says what just happened, and fills the beat before the cards. Without it
  // the delay reads as the game having stopped responding rather than as the
  // wave being over.
  world.showToast(
    `WAVE ${run.wave} CLEARED`,
    run.hurtThisWave ? 'HOLDING' : 'NOT A SCRATCH  ·  +500',
    run.hurtThisWave ? '#5fe4e0' : '#ffb454',
  );

  {
    // Three distinct cards that still have a rank left.
    const pool = RUN_CARDS.filter((c) => (run.taken.get(c.id) ?? 0) < c.ranks.length);
    run.offer = [];
    for (let i = 0; i < 3 && pool.length; i++) {
      run.offer.push(pool.splice(Math.floor(run.rng.next() * pool.length), 1)[0]);
    }
  }
}

/**
 * The arena fences the player; this fences the tide.
 *
 * Hunters arc wide when they attack, and a wide arc off a band centred on a
 * boat near the surface puts one of them in the air — measured at y +20 with
 * the waterline at 0, diving back down, which reads as a bug for the second it
 * lasts. Velocity is killed as well as position, or the same hunter climbs
 * straight back out on the next frame.
 *
 * Cheap enough to run over the whole pool: no field sampling, no allocation,
 * one comparison per axis per enemy.
 */
function contain(world: World) {
  const b = SURVIVAL_CONTENT.bounds!;
  const top = b.y1 - 4;
  for (const e of world.actors.enemies) {
    if (!e.alive || e.boss) continue;
    if (e.y > top) { e.y = top; if (e.vy > 0) e.vy = 0; }
    else if (e.y < b.y0) { e.y = b.y0; if (e.vy < 0) e.vy = 0; }
    // West of the line is where they are trying to get, so the far edge is
    // generous — this only stops one leaving the arena entirely.
    if (e.x < b.x0 - 20) { e.x = b.x0 - 20; if (e.vx < 0) e.vx = 0; }
    // Room for the approach lane the tide enters from.
    else if (e.x > b.x1 + 120) { e.x = b.x1 + 120; if (e.vx > 0) e.vx = 0; }
  }
}

/**
 * Pull the camera back far enough to see what is coming.
 *
 * The tide enters 45-95 m outside the fence and closes from there. At the
 * default framing that whole approach happens off-screen: things were dying
 * before the player ever saw them, which makes a wave feel like a number going
 * down rather than a fight. So the zoom follows the front — specifically the
 * nearest quarter of it, because the leaders are what the player is actually
 * about to deal with and one straggler out at the spawn line should not hold
 * the camera wide for the whole wave.
 *
 * `requestZoom` is a per-frame max that `World` clears after reading, so this
 * composes with the nitro zoom and the boss framing instead of fighting them.
 */
const BIAS_NEAR = 20;
const BIAS_FAR = 36;

function frameTheFront(world: World) {
  const p = world.player;
  let near = Infinity, count = 0, sum = 0;
  for (const e of world.actors.enemies) {
    if (!e.alive) continue;
    const d = Math.abs(e.x - p.x);
    if (d < near) near = d;
    if (d < 190) { sum += d; count++; }
  }
  if (!count) { world.camera.bias = BIAS_NEAR; return; }
  // Mean range of the engaged front, floored by the closest thing to us.
  const mean = Math.max(near, sum / count);
  const t = Math.min(1, Math.max(0, (mean - 55) / 80));

  // Two levers, and they are not equally priced.
  //
  // The BIAS is free: sliding the camera east shows more of the approach
  // without drawing a single extra pixel, so it does most of the work.
  //
  // The ZOOM is not. This renderer is fill-bound — that is the measured
  // bottleneck, not geometry and not CPU — so every 10% of zoom is 10% more
  // water, terrain and scenery to shade, on a phone that is already the thing
  // under pressure. It gets a deliberately short leash, and the bias covers
  // the rest.
  world.camera.bias = BIAS_NEAR + (BIAS_FAR - BIAS_NEAR) * t;
  world.requestZoom(1 + t * 0.3);
}

/**
 * Hold the front at strength for as long as the budget lasts.
 *
 * Metered in seconds rather than per frame. Six a frame put a whole front in
 * the water inside half a second, which arrives as one pile rather than as
 * something coming at you — and a pile is both unreadable and unfightable. The
 * rate fills a front in about five seconds and then replaces losses at the same
 * pace, so the tide reads as a stream with depth to it.
 */
function feedFront(world: World, dt: number) {
  const w = waveAt(run.wave);
  const tough = w.tough ?? 1;
  const rate = Math.min(8, Math.max(2.5, w.front / 5));
  run.spawnAcc = Math.min(run.spawnAcc + rate * dt, 4);

  while (run.spawnAcc >= 1 && run.sent < w.budget && liveAll < w.front) {
    // A carrier comes due at fixed fractions of the wave's supply.
    const progress = run.sent / w.budget;
    if (run.carriers.length && progress >= run.nextCarrierAt) {
      const c = run.carriers.shift()!;
      if (release(world, c.type, [-4, 10], c.tough, c)) {
        run.nextCarrierAt = progress + 0.9 / (run.carriers.length + 1);
        run.spawnAcc -= 1;
        continue;
      }
      run.carriers.unshift(c);
    }
    // A species at its cap must not stall the whole front — try another. This
    // used to `break`, so once four sentinels were alive every sentinel roll
    // ended the frame's feeding and the front quietly ran under strength.
    let placed = false;
    for (let attempt = 0; attempt < 5 && !placed; attempt++) {
      const m = pickMix(w.mix, run.rng.next());
      placed = release(world, m.type, m.band, tough * (m.tough ?? 1));
    }
    if (!placed) break;
    run.spawnAcc -= 1;
  }
}

export const SurvivalRules: ModeRules = {
  id: 'survival',
  label: 'SURVIVAL',
  blurb: 'HOLD A STRETCH OF THE SHELF FOR AS LONG AS YOU CAN',
  content: SURVIVAL_CONTENT,
  persists: false,

  begin(world: World) {
    run = blankRun();
    const p = world.player;
    p.mods.clear('run');
    p.mods.clear('timed');
    p.applyStats(statsFor(RUN_LEVELS, p.chassis));
    p.spawn(SURVIVAL_CONTENT.spawn.x, SURVIVAL_CONTENT.spawn.y, 2.5);
    p.lives = 1;
    p.torpedoes = Math.min(p.stats.torpedoMax, 8);
    p.charges = 0;
    // The tide only ever comes from the east, so the camera leans that way and
    // the boat sits left of centre. 20 m of a roughly 90 m view: about a quarter
    // of the screen traded from the empty water behind for the water the front
    // is forming up in.
    world.camera.bias = 20;
    world.camera.snapTo(p.x, p.y);
    // Asleep until its wave. The arena lair is 20 m from the spawn, so the
    // proximity wake that is fine at the bottom of a 7.9 km trench fires
    // instantly here — the thing has to be held down until wave 10 asks.
    world.octopus.retire();

    // Loot comes to you. The drops land inside the front, and flying into a
    // horde to fetch the shield cell you need *because* you are being
    // overwhelmed is exactly backwards — 26 m becomes 91.
    world.actors.magnetScale = 3.5;
    // What a kill is worth here. Salvage is suppressed because the mode has no
    // currency to spend it in, and its slot goes to the things that keep you
    // alive: shields first, by a distance.
    world.actors.suppressSalvage = true;
    world.actors.extraDrops = [
      { type: 'shield', chance: 0.15 },
      { type: 'repair', chance: 0.07 },
      { type: 'energy', chance: 0.06 },
      { type: 'torpedo', chance: 0.05 },
    ];
  },

  wave() { return run.wave; },
  ended(world: World) { return run.phase === 'over' || world.player.lives < 0; },

  tick(world: World, dt: number) {
    const p = world.player;
    const b = SURVIVAL_CONTENT.bounds!;

    // --- the fence ---------------------------------------------------------
    // A position and velocity rule, not terrain and not a current: `hazards`
    // caps its push at `h.power`, and the strongest authored current in the
    // game is 16 against 133 m/s² of nitro thrust. A current cannot fence
    // anything. This can: 150 m/s² inward wins by 13 %.
    const over = Math.max(b.x0 - p.x, p.x - b.x1, b.y0 - p.y, p.y - b.y1);
    run.warn = over < -14 ? 0 : Math.min(1, 1 + over / 14);
    if (over >= 0) {
      const nx = p.x < b.x0 ? 1 : p.x > b.x1 ? -1 : 0;
      const ny = p.y < b.y0 ? 1 : p.y > b.y1 ? -1 : 0;
      p.vx += nx * 150 * dt;
      p.vy += ny * 150 * dt;
      if (nx * p.vx < 0) p.vx -= p.vx * 6 * dt;
      if (ny * p.vy < 0) p.vy -= p.vy * 6 * dt;
      p.x = Math.min(b.x1 + 24, Math.max(b.x0 - 24, p.x));
      p.y = Math.min(b.y1 + 24, Math.max(b.y0 - 24, p.y));
      run.outsideFor += dt;
      // Continuous, no invulnerability frames — the vent-plume precedent, so
      // sitting outside the line is never free.
      if (run.outsideFor > 1.5) p.graze(12 * dt);
    } else {
      run.outsideFor = 0;
    }

    if (run.bossBanner > 0) run.bossBanner -= dt;
    if (run.chainT > 0) { run.chainT -= dt; if (run.chainT <= 0) run.chain = 0; }

    if (p.dead) { run.phase = 'over'; return; }

    run.t += dt;

    census(world);
    contain(world);
    frameTheFront(world);

    if (run.phase === 'gap') {
      // A card on offer holds the gap open until it is taken.
      if (run.offer.length) return;
      if (run.t >= INTERMISSION) {
        // Patch up, inline. `World.dock` is never called: it saves.
        p.repair(p.stats.maxHull * 0.45);
        p.rechargeShield();
        p.addEnergy(p.stats.maxEnergy);
        p.addNitro(p.stats.nitroMax);
        p.torpedoes = Math.max(p.torpedoes, Math.min(p.stats.torpedoMax, 8));
        if (p.stats.chargeUnlocked) p.charges = Math.max(p.charges, 2);
        beginWave(world, run.wave + 1);
      }
      return;
    }

    // --- the tide ----------------------------------------------------------
    feedFront(world, dt);

    const w = waveAt(run.wave);
    const octoAlive = w.octopus && world.octopus.awake && world.octopus.state !== 'dead';
    // Spent AND cleared. Out-killing the supply is the win condition, so a wave
    // is as long as the player is slow — which is what makes the front feel like
    // pressure rather than a queue.
    if (run.sent >= w.budget && liveAll === 0 && !octoAlive && run.t > 3) endWave(world);
  },
};

/** Fold a kill into the score. Called by World, which owns `onKill`. */
export function survivalKill(score: number) {
  run.chain++;
  run.chainT = 4;
  run.score += Math.round(score * (1 + 0.25 * Math.min(12, run.chain)));
}

/** Note that the player took damage, for the untouched-wave bonus. */
export function survivalHurt() { run.hurtThisWave = true; }

// ---------------------------------------------------------------------------
// The record
//
// Its own key, and its own shape. It holds a score, a wave, a time and two
// counters — no `Levels`, no salvage, no craters, no modifiers. The shape of
// the record IS the proof that a run cannot pay itself forward into permanence.
// ---------------------------------------------------------------------------

const SKEY = 'abyss.survival.v1';

export interface SurvivalRecord {
  v: number;
  bestScore: number;
  bestWave: number;
  bestTime: number;
  runs: number;
  kills: number;
}

export function loadSurvivalRecord(): SurvivalRecord {
  const blank: SurvivalRecord = { v: 1, bestScore: 0, bestWave: 0, bestTime: 0, runs: 0, kills: 0 };
  try {
    const raw = window.localStorage.getItem(SKEY);
    if (!raw) return blank;
    const d = JSON.parse(raw) as Partial<SurvivalRecord>;
    return d.v === 1 ? { ...blank, ...d } : blank;
  } catch {
    return blank;
  }
}

/**
 * Close a run out: record it, and pay the profile.
 *
 * Credits are the ONE thing that crosses out of a run, and they cross as money
 * rather than as power — which is exactly the PRD's split. A card is gone the
 * moment the run ends; what you earned with it can buy a boat.
 */
export function bankSurvival(world: World): { score: number; wave: number; credits: number } {
  const score = run.score;
  const wave = run.wave;
  const credits = Math.round(score * 0.02);

  const rec = loadSurvivalRecord();
  rec.runs++;
  rec.kills += world.actors.kills;
  rec.bestScore = Math.max(rec.bestScore, score);
  rec.bestWave = Math.max(rec.bestWave, wave);
  rec.bestTime = Math.max(rec.bestTime, world.playTime);
  try { window.localStorage.setItem(SKEY, JSON.stringify(rec)); } catch { /* quota */ }

  bank(world.profile, credits, 'survival', score);
  return { score, wave, credits };
}
